import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../utils';
import { useT } from '../../i18n';
import type { CarouselButtonConfig, CarouselItem } from '../../types';
import { DisplayCard } from './DisplayCard';
import { ProgramCard } from '../Searchbar/ProgramCard';
import styles from './DisplayCarousel.module.css';

export interface DisplayCarouselProps {
  items: CarouselItem[];
  onItemClick?: (item: CarouselItem) => void;
  onSeeMoreClick?: () => void;
  showSeeMore?: boolean;
  button?: CarouselButtonConfig;
  onButtonClick?: (item: CarouselItem, formId: string) => void;
  /** Visual variant. 'program-card' renders the IEBS-style searchbar grid. */
  variant?: 'default' | 'program-card';
  /** Custom arrow image for program-card hover (null = default arrow icon). */
  hoverArrowUrl?: string | null;
  className?: string;
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

function RefreshCCWIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </svg>
  );
}

export function DisplayCarousel({
  items,
  onItemClick,
  onSeeMoreClick,
  showSeeMore = false,
  button,
  onButtonClick,
  variant = 'default',
  hoverArrowUrl,
  className,
}: DisplayCarouselProps) {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const hasImages = items.some(item => !!item.imageUrl);

  const checkScrollPosition = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollBy = useCallback((factor: number) => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * factor;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  const scrollLeft = useCallback(() => scrollBy(-0.8), [scrollBy]);
  const scrollRight = useCallback(() => scrollBy(0.8), [scrollBy]);

  useEffect(() => {
    checkScrollPosition();

    const container = scrollRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition, items]);

  const handleItemClick = useCallback((item: CarouselItem) => {
    onItemClick?.(item);
  }, [onItemClick]);

  // Searchbar program-card grid: skip image-led / scroll branches entirely.
  if (variant === 'program-card') {
    return (
      <div className={cn(styles.programGridContainer, className)}>
        <div className={styles.programGrid}>
          {items.map((item) => (
            <ProgramCard key={item.id} item={item} onClick={onItemClick} hoverArrowUrl={hoverArrowUrl} />
          ))}
        </div>
      </div>
    );
  }

  if (!hasImages) {
    return (
      <div className={cn(styles.container, className)}>
        <div className={styles.verticalList}>
          {items.map(item => (
            <DisplayCard
              key={item.id}
              item={item}
              variant="vertical"
              onClick={handleItemClick}
              button={button}
              onButtonClick={onButtonClick}
            />
          ))}
        </div>

        {showSeeMore && onSeeMoreClick && (
          <div className={styles.seeMore}>
            <button
              className={styles.seeMoreButton}
              onClick={onSeeMoreClick}
              type="button"
            >
              {t('carousel.seeMore')}
              <RefreshCCWIcon className={styles.seeMoreIcon} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn(styles.container, className)}>
      <AnimatePresence>
        {canScrollLeft && (
          <motion.div
            key="left-arrow"
            className={cn(styles.arrowWrapper, styles.arrowWrapperLeft)}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <motion.button
              className={styles.arrow}
              onClick={scrollLeft}
              type="button"
              aria-label={t('carousel.scrollLeft')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeftIcon className={styles.arrowIcon} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {canScrollRight && (
          <motion.div
            key="right-arrow"
            className={cn(styles.arrowWrapper, styles.arrowWrapperRight)}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <motion.button
              className={styles.arrow}
              onClick={scrollRight}
              type="button"
              aria-label={t('carousel.scrollRight')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRightIcon className={styles.arrowIcon} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <div ref={scrollRef} className={styles.scrollContainer}>
        {items.map(item => (
          <DisplayCard
            key={item.id}
            item={item}
            variant="horizontal"
            onClick={handleItemClick}
            button={button}
            onButtonClick={onButtonClick}
          />
        ))}
      </div>

      {showSeeMore && onSeeMoreClick && (
        <div className={styles.seeMore}>
          <button
            className={styles.seeMoreButton}
            onClick={onSeeMoreClick}
            type="button"
          >
            {t('carousel.seeMore')}
            <RefreshCCWIcon className={styles.seeMoreIcon} />
          </button>
        </div>
      )}
    </div>
  );
}
